import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

export const useUserRole = () => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAttorney, setIsAttorney] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [isDelegate, setIsDelegate] = useState(false);
  const [isInHouseCounsel, setIsInHouseCounsel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lawyerData, setLawyerData] = useState<any>(null);
  const [lawFirmData, setLawFirmData] = useState<any>(null);
  const [delegateData, setDelegateData] = useState<any>(null);
  const [inHouseCounselData, setInHouseCounselData] = useState<any>(null);

  useEffect(() => {
    let mounted = true;
    
    const checkUserRoles = async () => {
      console.log('[useUserRole] 🔍 Starting role check for user:', {
        hasUser: !!user,
        userId: user?.id,
        email: user?.email
      });

      if (!user) {
        console.log('[useUserRole] ❌ No user found, setting defaults');
        if (mounted) {
          setIsAdmin(false);
          setIsAttorney(false);
          setIsClient(false);
          setIsDelegate(false);
          setIsInHouseCounsel(false);
          setLoading(false);
        }
        return;
      }

      // CRITICAL: mark loading=true at the start of every role lookup for a
      // signed-in user. Otherwise, when the user transitions from null →
      // authenticated, `loading` is still false from the previous "no user"
      // branch and consumers (Auth.tsx redirect, portal resolution) will read
      // stale role defaults — causing a brief flash of the client portal for
      // attorneys/admins/in-house before roles resolve.
      if (mounted) setLoading(true);

      try {
        // Fetch roles, lawyer info, delegate info, and in-house counsel info in parallel
        const [rolesResult, lawyerResult, delegateResult, inHouseResult] = await Promise.allSettled([
          supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', user.id),
          supabase
            .from('lawyers')
            .select(`
              *,
              firms:firm_id (
                id,
                name,
                is_active
              )
            `)
            .eq('user_id', user.id)
            .eq('is_active', true)
            .maybeSingle(),
          supabase
            .from('client_delegates')
            .select(`
              *,
              clients:client_id (
                id,
                first_name,
                last_name,
                company,
                user_id
              ),
              firms:firm_id (
                id,
                name,
                is_active
              )
            `)
            .eq('user_id', user.id)
            .eq('is_active', true)
            .maybeSingle(),
          supabase
            .from('in_house_counsel')
            .select('*')
            .eq('user_id', user.id)
            .eq('is_active', true)
            .maybeSingle()
        ]);

        const roles = rolesResult.status === 'fulfilled' ? rolesResult.value.data : [];
        const rolesError = rolesResult.status === 'fulfilled' ? rolesResult.value.error : null;
        const lawyerInfo = lawyerResult.status === 'fulfilled' ? lawyerResult.value.data : null;
        const lawyerError = lawyerResult.status === 'fulfilled' ? lawyerResult.value.error : null;
        const delegateInfo = delegateResult.status === 'fulfilled' ? delegateResult.value.data : null;
        const inHouseInfo = inHouseResult.status === 'fulfilled' ? inHouseResult.value.data : null;

        if (rolesError) {
          console.warn('[useUserRole] ⚠️ Error fetching roles:', rolesError);
        }
        if (lawyerError) {
          console.warn('[useUserRole] ⚠️ Error fetching lawyer info:', lawyerError);
        }

        const userRoles = roles?.map((r: any) => r.role) || [];
        const hasActiveLawyer = !lawyerError && !!lawyerInfo;
        const hasActiveDelegate = !!delegateInfo;
        const hasActiveInHouse = !!inHouseInfo;
        
        const isAdminRole = userRoles.includes('admin');
        const isAttorneyRole =
          hasActiveLawyer ||
          userRoles.includes('lawyer') ||
          userRoles.includes('law_firm_admin') ||
          userRoles.includes('law_firm_member');
        const isInHouseRole = hasActiveInHouse || userRoles.includes('in_house_counsel');
        const isDelegateRole = hasActiveDelegate && !isAdminRole && !isAttorneyRole && !isInHouseRole;
        const isClientRole = !isAdminRole && !isAttorneyRole && !isInHouseRole && !isDelegateRole;

        if (mounted) {
          setIsAdmin(isAdminRole);
          setIsAttorney(isAttorneyRole);
          setIsClient(isClientRole);
          setIsDelegate(isDelegateRole);
          setIsInHouseCounsel(isInHouseRole);
          setLawyerData(hasActiveLawyer ? lawyerInfo : null);
          setLawFirmData(hasActiveLawyer && lawyerInfo?.firms ? lawyerInfo.firms : null);
          setDelegateData(hasActiveDelegate ? delegateInfo : null);
          setInHouseCounselData(hasActiveInHouse ? inHouseInfo : null);
          setLoading(false);
        }

      } catch (error) {
        console.error('[useUserRole] ❌ Error in checkUserRoles:', error);
        if (mounted) {
          setIsAdmin(false);
          setIsAttorney(false);
          setIsClient(false);
          setIsDelegate(false);
          setIsInHouseCounsel(false);
          setLawyerData(null);
          setLawFirmData(null);
          setDelegateData(null);
          setInHouseCounselData(null);
          setLoading(false);
        }
      }
    };

    checkUserRoles();
    return () => { mounted = false; };
  }, [user?.id]);

  const hasAttorneyAccess = () => isAdmin || isAttorney;
  const hasClientAccess = () => !user || isClient || isDelegate || (!isAttorney && !isAdmin);
  
  const isExistingLawyer = () => {
    return lawyerData && lawyerData.is_active;
  };

  const hasActiveLawFirm = () => {
    return lawFirmData && lawFirmData.is_active;
  };

  const isTrialExpired = () => {
    return false;
  };

  const hasDelegatePermission = (permission: string) => {
    if (!isDelegate || !delegateData) return false;
    return (delegateData.permissions || []).includes(permission);
  };

  return { 
    isAdmin, 
    isAttorney, 
    isClient,
    isDelegate,
    isInHouseCounsel,
    loading, 
    hasAttorneyAccess, 
    hasClientAccess,
    lawyerData,
    lawFirmData,
    delegateData,
    inHouseCounselData,
    isExistingLawyer,
    hasActiveLawFirm,
    isTrialExpired,
    hasDelegatePermission
  };
};